import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Modal,
  Stack,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {WHITE_100} from '../../theme';

const modalStyles = {
  base: {
    borderRadius: '0.5rem',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
    backgroundColor: WHITE_100,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: '700',
    lineHeight: '120%',
    color: '#043B34',
  },
  divider: {
    borderBottomWidth: '0.0625rem',
    opacity: 1,
    color: '#DDe0DE',
  },
  label: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#043B34',
    marginBottom: '8px',
  },
  value: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#162523',
    marginBottom: '16px',
  },
  cancelButton: {
    borderRadius: '0.5rem',
    paddingVertical: '0.6825rem',
    paddingHorizontal: '1rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: WHITE_100,
    },
  },
  cancelButtonLabel: {
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '130%',
  },
  clientItem: {
    padding: '12px 0',
    borderBottom: '1px solid #E1E5E4',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  clientName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#162523',
    marginBottom: '4px',
  },
  clientRole: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#819190',
  },
};

const UpdateUserModal = ({
  open,
  onClose,
  user,
  memberships,
  userData,
  parentOrgId,
}) => {
  if (!user) return null;

  // Get name from userData if available, otherwise fall back to email
  let userName = 'Unknown';
  if (userData?.name) {
    const {firstName, lastName} = userData.name;
    if (firstName && lastName) {
      userName = `${firstName} ${lastName}`;
    } else {
      userName =
        firstName ||
        lastName ||
        user.email?.split('@')[0] ||
        user.email ||
        'Unknown';
    }
  } else {
    userName = user.email?.split('@')[0] || user.email || 'Unknown';
  }

  // Filter out the parent org from memberships and sort alphabetically by orgName
  const clientMemberships = memberships
    .filter((membership) => membership.orgId !== parentOrgId)
    .sort((a, b) => {
      const nameA = (a.orgName || a.orgId || '').toLowerCase();
      const nameB = (b.orgName || b.orgId || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus disableEnforceFocus>
      <Box
        position="absolute"
        sx={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
        }}
      >
        <Box
          width="40rem"
          minWidth="40rem"
          maxWidth="40rem"
          sx={{outline: 'none'}}
        >
          <Stack direction="column" sx={modalStyles.base}>
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={modalStyles.title}>User Details</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="column"
              paddingTop="1.25rem"
              paddingBottom="1.5rem"
              paddingX="1.5rem"
              spacing="1rem"
            >
              {/* Name and Email */}
              <Stack direction="row" spacing="2rem" alignItems="flex-start">
                <Stack direction="column" sx={{flex: 1}}>
                  <Typography sx={modalStyles.label}>Name</Typography>
                  <Typography sx={modalStyles.value}>{userName}</Typography>
                </Stack>
                <Stack direction="column" sx={{flex: 1}}>
                  <Typography sx={modalStyles.label}>Email</Typography>
                  <Typography sx={modalStyles.value}>{user.email}</Typography>
                </Stack>
              </Stack>

              {/* Client Organizations */}
              <Stack direction="column">
                <Typography sx={modalStyles.label}>Clients</Typography>
                {clientMemberships && clientMemberships.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: '40vh',
                      overflowY: 'auto',
                      marginTop: '8px',
                      paddingRight: '16px',
                      marginRight: '-8px',
                    }}
                  >
                    <List sx={{padding: 0}}>
                      {clientMemberships.map((membership, index) => (
                        <ListItem key={index} sx={modalStyles.clientItem}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <Typography sx={modalStyles.clientName}>
                              {membership.orgName || membership.orgId}
                            </Typography>
                            <Typography sx={modalStyles.clientRole}>
                              {membership.role || 'N/A'}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      ...modalStyles.value,
                      color: '#819190',
                      fontStyle: 'italic',
                    }}
                  >
                    No client organizations
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant="outlined"
                sx={{
                  ...modalStyles.cancelButton,
                  '&:focus': {
                    outline: 'none',
                  },
                }}
                onClick={onClose}
              >
                <Typography style={modalStyles.cancelButtonLabel}>
                  Close
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

UpdateUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    user_id: PropTypes.string,
    email: PropTypes.string.isRequired,
  }),
  memberships: PropTypes.arrayOf(
    PropTypes.shape({
      orgId: PropTypes.string,
      orgName: PropTypes.string,
      role: PropTypes.string,
    })
  ),
  userData: PropTypes.shape({
    name: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
  parentOrgId: PropTypes.string,
};

UpdateUserModal.defaultProps = {
  user: null,
  memberships: [],
  userData: null,
  parentOrgId: null,
};

export default UpdateUserModal;
